import { gql } from "@apollo/client";
import { isValid, parseISO, subMonths } from "date-fns";
import { format } from "date-fns-tz";
import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useSearchParams } from "react-router-dom";
import { DATE_FORMAT, PAGE_SIZE } from "../../_lib/constants";
import { RegisterTableHeader } from "../../_lib/register-table-header";
import { useNigeriaDailyOutpatientRegisterLazyQuery } from "./__generated__/page.gql";
import { NigeriaDailyOutpatientRegisterTable } from "./_lib/NigeriaDailyOutpatientRegisterTable";

gql`
  query NigeriaDailyOutpatientRegister($input: NigeriaDailyOutpatientRegisterInput!) {
    nigeriaDailyOutpatientRegister(input: $input) {
      limit
      offset
      total
      records {
        id
        date
        patient {
          uuid
          person {
            name {
              fullname
            }
            dob
            gender
          }
          cardId
          passport {
            uid
          }
        }
        isPregnant
        attendanceType
        weight
        height
        BMI
        presentingComplaint
        investigationsCompleted
        diagnosis
        drugsGiven
        outcomeOfVisit
        malariaDiagnosis
        actGiven
        otherAntimalarialGiven
        severeMalaria
        preReferralTreatment
        tbClinicallyScreened
        tbClinicalScreeningForm
        tbReferred
        hepatitisB
        hepatitisC
        gbvCaseSeen
        postGBVCareReceived
        gbvCaseReferred
      }
    }
  }
`;

export const NigeriaDailyOutpatientRegister = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const startDateParam = searchParams.get("startDate") ?? "";
  const endDateParam = searchParams.get("endDate") ?? "";
  const currentPageParam = Number(searchParams.get("currentPage"));
  const [currentPage, setCurrentPage] = useState(
    !Number.isNaN(currentPageParam) ? currentPageParam : 0,
  );
  const [startDate, setStartDate] = useState(
    isValid(parseISO(startDateParam))
      ? startDateParam
      : format(subMonths(new Date(), 1), DATE_FORMAT),
  );
  const [endDate, setEndDate] = useState(
    isValid(parseISO(endDateParam))
      ? endDateParam
      : format(new Date(), DATE_FORMAT),
  );

  const [fetchRecords, { data, loading }] =
    useNigeriaDailyOutpatientRegisterLazyQuery();

  useEffect(() => {
    if (startDate && endDate) {
      fetchRecords({
        variables: {
          input: {
            filterOptions: {
              startDate,
              endDate,
            },
            paginationOptions: {
              offset: currentPage * PAGE_SIZE,
              limit: PAGE_SIZE,
            },
          },
        },
      });
    }
  }, [startDate, endDate, currentPage, fetchRecords]);

  useEffect(() => {
    setSearchParams({ startDate, endDate, currentPage: `${currentPage}` });
  }, [startDate, endDate, currentPage, setSearchParams]);

  return (
    <>
      <RegisterTableHeader
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        setCurrentPage={setCurrentPage}
        heading={
          <FormattedMessage
            id="register.nigeriaDailyOutpatient.title"
            defaultMessage="Daily Outpatient Register"
          />
        }
        total={data?.nigeriaDailyOutpatientRegister?.total ?? 0}
      />

      <NigeriaDailyOutpatientRegisterTable
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        loading={loading}
        data={data}
      />
    </>
  );
};

export default NigeriaDailyOutpatientRegister;
